import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../AdminUserManagement.css';

const API_URL = 'http://localhost:5000';

function AdminUserManagement({ token, onLogout, isEmbedded = false }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Add user form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'contributor' });
  const [submitting, setSubmitting] = useState(false);

  // Edit user form state
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', email: '', role: 'contributor' });
  const [editFormError, setEditFormError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
    } catch (err) {
      console.error('Fetch users error:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      setUpdatingId(userId);
      setError('');

      await axios.put(
        `${API_URL}/api/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setUsers(users.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));

      setSuccessMessage(`User role updated to ${newRole}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Update role error:', err);
      setError(err.response?.data?.message || 'Failed to update role');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusChange = async (userId, currentStatus) => {
    try {
      setUpdatingId(userId);
      setError('');
      const newStatus = !currentStatus;

      await axios.put(
        `${API_URL}/api/users/${userId}/status`,
        { is_active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setUsers(users.map(u =>
        u.id === userId ? { ...u, is_active: newStatus } : u
      ));

      setSuccessMessage(`User ${newStatus ? 'activated' : 'deactivated'}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Update status error:', err);
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError('');

      await axios.post(
        `${API_URL}/api/users`,
        {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh user list
      await fetchUsers();

      // Reset form
      setFormData({ name: '', email: '', password: '', role: 'contributor' });
      setShowAddForm(false);

      setSuccessMessage(`User ${formData.name} created successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Add user error:', err);
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditUser = (user) => {
    setEditingId(user.id);
    setEditFormData({
      name: user.name,
      email: user.email,
      role: user.role,
    });
    setEditFormError('');
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingId) return;

    try {
      setUpdatingId(editingId);
      setEditFormError('');

      await axios.put(
        `${API_URL}/api/users/${editingId}`,
        {
          name: editFormData.name,
          email: editFormData.email,
          role: editFormData.role,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setUsers(users.map(u =>
        u.id === editingId
          ? { ...u, name: editFormData.name, email: editFormData.email, role: editFormData.role }
          : u
      ));

      setSuccessMessage(`User updated successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setEditingId(null);
    } catch (err) {
      console.error('Edit user error:', err);
      setEditFormError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-user-management">
        <div className="loading">Loading user management...</div>
      </div>
    );
  }

  return (
    <div className="admin-user-management">
      {!isEmbedded && (
        <div className="admin-header">
          <div>
            <h1>User Management</h1>
            <p className="subtitle">Manage system users and assign roles</p>
          </div>
          <button className="logout-btn" onClick={onLogout}>
            Logout
          </button>
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}
      {successMessage && <div className="success-banner">{successMessage}</div>}

      <div className="users-section">
        <div className="users-header">
          <h2>All Users ({users.length})</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? '✕ Cancel' : '+ Add New User'}
          </button>
        </div>

        {showAddForm && (
          <form className="add-user-form" onSubmit={handleAddUser}>
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password (min 8 characters)"
                required
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="contributor">Contributor</option>
                <option value="facilitator">Facilitator</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </form>
        )}

        {users.length === 0 ? (
          <div className="empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={!user.is_active ? 'inactive' : ''}>
                    <td className="user-name">{user.name}</td>
                    <td className="user-email">{user.email}</td>
                    <td className="user-role">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={updatingId === user.id}
                        className="role-select"
                      >
                        <option value="admin">Admin</option>
                        <option value="facilitator">Facilitator</option>
                        <option value="contributor">Contributor</option>
                      </select>
                    </td>
                    <td className="user-status">
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="user-created">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="user-actions">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => startEditUser(user)}
                        disabled={updatingId === user.id}
                      >
                        Edit
                      </button>
                      <button
                        className={`status-btn ${user.is_active ? 'deactivate' : 'activate'}`}
                        onClick={() => handleStatusChange(user.id, user.is_active)}
                        disabled={updatingId === user.id}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editingId && (
          <div className="edit-user-modal">
            <div className="modal-content">
              <h3>Edit User</h3>
              {editFormError && <div className="form-error">{editFormError}</div>}

              <form onSubmit={handleEditUser} className="edit-user-form">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                  >
                    <option value="admin">Admin</option>
                    <option value="facilitator">Facilitator</option>
                    <option value="contributor">Contributor</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={updatingId === editingId}>
                    Save Changes
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setEditingId(null)}
                    disabled={updatingId === editingId}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUserManagement;
